import React from 'react';
import styled, { keyframes } from 'styled-components';

const AIWebinarCTA = () => {
  return (
    <CTASection id="webinar-signup">
      <OverlayGradient />
      <ContentContainer>
        <LeftColumn>
          <CTABadge>FREE REGISTRATION</CTABadge>
          <CTATitle>Join Our AI Business Transformation Webinar</CTATitle>
          <CTADescription>
            Learn practical strategies to implement AI in your business operations and stay ahead of your competition.
          </CTADescription>
          
          <BenefitsList>
            <BenefitItem>
              <BenefitIcon>💡</BenefitIcon>
              <BenefitText>Discover AI tools that work for your specific business</BenefitText>
            </BenefitItem>
            <BenefitItem>
              <BenefitIcon>💰</BenefitIcon>
              <BenefitText>Learn how to save time & money with automation</BenefitText>
            </BenefitItem>
            <BenefitItem>
              <BenefitIcon>🚀</BenefitIcon>
              <BenefitText>Get actionable strategies you can implement immediately</BenefitText>
            </BenefitItem>
            <BenefitItem>
              <BenefitIcon>🔍</BenefitIcon>
              <BenefitText>Find out if custom AI solutions are right for you</BenefitText>
            </BenefitItem>
          </BenefitsList>
          
          <WebinarDetails>
            <DetailItem>
              <DetailIcon>📅</DetailIcon>
              <DetailText>Available Weekly</DetailText>
            </DetailItem>
            <DetailItem>
              <DetailIcon>⏱️</DetailIcon>
              <DetailText>30 Minutes + Q&A</DetailText>
            </DetailItem>
            <DetailItem>
              <DetailIcon>💻</DetailIcon>
              <DetailText>Online via Zoom</DetailText>
            </DetailItem>
          </WebinarDetails>
        </LeftColumn>
        
        <RightColumn>
          <CalendlyContainer>
            <FormHeader>
              <FormTitle>Reserve Your Spot</FormTitle>
              <FormSubtitle>Choose a date and time that works for you</FormSubtitle>
            </FormHeader>
            
            <CalendlyButton 
              href="https://calendly.com/pmteamwinners/ai-for-better-business-webinar" 
              target="_blank"
              rel="noopener noreferrer"
            >
              SCHEDULE YOUR WEBINAR
              <ArrowIcon>→</ArrowIcon>
            </CalendlyButton>
            
            <CalendlyInfo>
              <InfoIcon>ℹ️</InfoIcon>
              <InfoText>You'll be redirected to Calendly to select your preferred date and time</InfoText>
            </CalendlyInfo>
          </CalendlyContainer>
          
          <Testimonial>
            <QuoteSymbol>"</QuoteSymbol>
            <TestimonialText>
              The AI strategies I learned in this webinar helped me automate 30% of my daily tasks. This free session was more valuable than courses I've paid hundreds for.
            </TestimonialText>
            <TestimonialAuthor>— Michael Roberts, Small Business Owner</TestimonialAuthor>
          </Testimonial>
        </RightColumn>
      </ContentContainer>
      
      <FloatingElements>
        <FloatingCircle top="10%" left="5%" size="100px" opacity="0.03" />
        <FloatingCircle top="70%" left="15%" size="150px" opacity="0.02" />
        <FloatingCircle top="30%" right="10%" size="120px" opacity="0.03" />
        <GridLines />
      </FloatingElements>
    </CTASection>
  );
};

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 255, 198, 0.7);
  }
  
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 15px rgba(0, 255, 198, 0);
  }
  
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 255, 198, 0);
  }
`;

const float = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0); }
`;

// Styled Components
const CTASection = styled.section`
  position: relative;
  padding: 100px 80px;
  background-color: #0a0e17;
  overflow: hidden;
  
  @media (max-width: 991px) {
    padding: 80px 40px;
  }
  
  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const OverlayGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(10, 14, 23, 0.8) 0%, rgba(15, 20, 35, 0.9) 100%);
  z-index: 1;
`;

const ContentContainer = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  max-width: 1200px;
  margin: 0 auto;
  gap: 60px;
  
  @media (max-width: 991px) {
    flex-direction: column;
    gap: 40px;
  }
`;

const LeftColumn = styled.div`
  flex: 1;
  animation: ${fadeIn} 0.8s ease-out;
`;

const RightColumn = styled.div`
  flex: 1;
  animation: ${fadeIn} 0.8s ease-out;
  animation-delay: 0.2s;
`;

const CTABadge = styled.div`
  display: inline-block;
  background: rgba(0, 255, 198, 0.1);
  color: #00FFC6;
  font-weight: 700;
  padding: 8px 20px;
  border-radius: 30px;
  font-size: 0.9rem;
  margin-bottom: 20px;
  border: 1px solid rgba(0, 255, 198, 0.2);
`;

const CTATitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 20px;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.2rem;
  color: #a0a0a0;
  line-height: 1.6;
  margin-bottom: 30px;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 40px;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  font-size: 1.1rem;
`;

const BenefitIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(0, 255, 198, 0.1);
  border-radius: 50%;
  margin-right: 15px;
  font-size: 1.2rem;
`;

const BenefitText = styled.span`
  color: #e0e0e0;
`;

const WebinarDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 30px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  padding: 10px 20px;
  border-radius: 8px;
  margin-right: 10px;
`;

const DetailIcon = styled.span`
  margin-right: 10px;
  font-size: 1.2rem;
`;

const DetailText = styled.span`
  color: #e0e0e0;
  font-size: 0.9rem;
  font-weight: 500;
`;

const CalendlyContainer = styled.div`
  background: linear-gradient(135deg, rgba(15, 20, 35, 0.95), rgba(10, 14, 23, 0.95));
  border-radius: 16px;
  padding: 40px;
  border: 1px solid rgba(0, 255, 198, 0.2);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const FormHeader = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const FormTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 10px;
`;

const FormSubtitle = styled.p`
  font-size: 1rem;
  color: #a0a0a0;
`;

const CalendlyButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #00FFC6, #0057FF);
  color: #000;
  font-weight: 700;
  font-size: 1rem;
  padding: 16px 30px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 10px 25px rgba(0, 255, 198, 0.3);
  animation: ${pulse} 2s infinite;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 255, 198, 0.4);
  }
`;

const ArrowIcon = styled.span`
  margin-left: 10px;
  font-size: 1.2rem;
  font-weight: bold;
  transition: transform 0.3s ease;
  
  ${CalendlyButton}:hover & {
    transform: translateX(5px);
  }
`;

const CalendlyInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
`;

const InfoIcon = styled.span`
  margin-right: 8px;
  font-size: 1rem;
`;

const InfoText = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.5);
`;

const Testimonial = styled.div`
  margin-top: 40px;
  background: rgba(15, 20, 35, 0.5);
  border-radius: 16px;
  padding: 30px;
  border: 1px solid rgba(0, 255, 198, 0.1);
  position: relative;
`;

const QuoteSymbol = styled.div`
  position: absolute;
  top: -15px;
  left: 20px;
  font-size: 4rem;
  color: rgba(0, 255, 198, 0.2);
  font-family: Georgia, serif;
`;

const TestimonialText = styled.p`
  font-size: 1rem;
  color: #e0e0e0;
  line-height: 1.6;
  font-style: italic;
  margin-bottom: 15px;
`;

const TestimonialAuthor = styled.p`
  font-size: 0.9rem;
  color: #00FFC6;
  text-align: right;
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
`;

const FloatingCircle = styled.div`
  position: absolute;
  width: ${props => props.size};
  height: ${props => props.size};
  border-radius: 50%;
  background: rgba(0, 255, 198, ${props => props.opacity});
  top: ${props => props.top};
  left: ${props => props.left};
  right: ${props => props.right};
  animation: ${float} 8s ease-in-out infinite;
`;

const GridLines = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(0, 255, 198, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 255, 198, 0.05) 1px, transparent 1px);
  background-size: 50px 50px;
  z-index: -1;
`;

export default AIWebinarCTA; 